import React, { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTask } from '../contexts/TaskContext';

// 日付をコンパクト表示（YYYY-MM-DD → M/D）
function formatDateCompact(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  return `${m}/${d}`;
}

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE = 10;

export default function TaskItem({ task }) {
  const { updateTaskById, deleteTaskById, moveTaskToOpposite } = useTask();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [isPressing, setIsPressing] = useState(false);
  const dateInputRef = useRef(null);
  const pressTimerRef = useRef(null);
  const pressStartRef = useRef(null);
  // label htmlFor と input id を紐付けるための一意 ID（タスクごとに固定）
  const dateInputId = `task-date-${task.id}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 長押しタイマーのクリーンアップ
  useEffect(() => {
    return () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    };
  }, []);

  // ===== タスク名の長押し（0.5秒）で今日⇔明日以降を移動 =====
  const cancelLongPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    pressStartRef.current = null;
    setIsPressing(false);
  };

  const handlePressStart = (e) => {
    if (isEditing) return;
    pressStartRef.current = { x: e.clientX, y: e.clientY };
    setIsPressing(true);
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      pressStartRef.current = null;
      setIsPressing(false);
      if (navigator.vibrate) navigator.vibrate(50);
      moveTaskToOpposite(task);
    }, LONG_PRESS_MS);
  };

  const handlePressMove = (e) => {
    if (!pressStartRef.current) return;
    const dx = Math.abs(e.clientX - pressStartRef.current.x);
    const dy = Math.abs(e.clientY - pressStartRef.current.y);
    // 指が動いたらスクロール操作とみなして長押しを中断
    if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
      cancelLongPress();
    }
  };

  const handleToggle = () => {
    updateTaskById(task.id, { completed: !task.completed });
  };

  const handleDelete = () => {
    deleteTaskById(task.id);
  };

  const handleEditSubmit = () => {
    if (editTitle.trim() && editTitle !== task.title) {
      updateTaskById(task.id, { title: editTitle.trim() });
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleEditSubmit();
    if (e.key === 'Escape') {
      setEditTitle(task.title);
      setIsEditing(false);
    }
  };

  // ユーザーが明示的に日付を選んだ時だけ確定する
  // （モバイルで開いた瞬間に当日が選ばれて即閉じる不具合を防ぐため、
  //   空→空のような実質変化なしのonChangeは無視する）
  const handleDateChange = (e) => {
    const newDate = e.target.value || null;
    const current = task.date || null;
    if (newDate === current) return; // 値が変わっていない＝誤発火とみなして無視
    updateTaskById(task.id, { date: newDate });
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    updateTaskById(task.id, { date: null });
  };

  // 日付UI領域でのポインタ操作は、タスク名側の長押し移動ロジックへ伝播させない。
  // （日付UIは .task-title の兄弟なので現状でも長押しは発火しないが、将来カード全体に
  //   ポインタハンドラが付いても日付タップが奪われないよう、明示的に伝播を止める保険。）
  const stopDatePropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`task-item ${task.completed ? 'completed' : ''} ${isDragging ? 'dragging' : ''} ${isPressing ? 'pressing' : ''}`}
    >
      <button className="drag-handle" {...attributes} {...listeners} aria-label="ドラッグして並び替え">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="5" cy="3" r="1.5" />
          <circle cx="11" cy="3" r="1.5" />
          <circle cx="5" cy="8" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="13" r="1.5" />
          <circle cx="11" cy="13" r="1.5" />
        </svg>
      </button>

      <label className="task-checkbox">
        <input
          type="checkbox"
          checked={task.completed}
          onChange={handleToggle}
        />
        <span className="checkmark" />
      </label>

      {isEditing ? (
        <input
          type="text"
          className="task-edit-input"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={handleEditSubmit}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      ) : (
        <span
          className="task-title"
          onDoubleClick={() => setIsEditing(true)}
          onPointerDown={handlePressStart}
          onPointerMove={handlePressMove}
          onPointerUp={cancelLongPress}
          onPointerLeave={cancelLongPress}
          onPointerCancel={cancelLongPress}
          onContextMenu={(e) => e.preventDefault()}
        >
          {task.title}
        </span>
      )}

      {/* 定期タスク由来の今日タスクに繰り返しバッジを表示 */}
      {task.recurringId && task.recurLabel && (
        <span className="task-recur-badge" title="定期タスク">
          <span className="task-recur-icon" aria-hidden="true">🔄</span>
          {task.recurLabel}
        </span>
      )}

      {/* 明日以降タスクの日付ボタン
          ＝ input[type=date] 本体を📅ボタンの見た目に重ねて直接配置する。
          input そのものをタップ＝HTML標準で OS のネイティブ日付ピッカーが必ず開く（JSハンドラ不要）。
          label→隠しinput 方式はモバイルで起動しないことがあったため input 直接化で根治。 */}
      {task.category === 'future' && (
        <div
          className={`task-date-area ${task.date ? 'has-date' : 'no-date'}`}
          onPointerDown={stopDatePropagation}
          onTouchStart={stopDatePropagation}
          onPointerUp={stopDatePropagation}
        >
          {/* 📅/日付テキストの見た目 ＋ その上に重ねた実体 input。
              input の絶対配置はこの trigger 内に限定し、解除×には被せない。 */}
          <div className="task-date-trigger">
            {/* 見た目の表示。pointer-events:none で必ず上の input がタップを受ける。 */}
            <span className="task-date-display" aria-hidden="true">
              {task.date ? formatDateCompact(task.date) : '📅'}
            </span>
            {/* 実体のネイティブ日付入力。実寸(44px)・タップ可能・透明。本体タップで OS ピッカーが開く。 */}
            <input
              id={dateInputId}
              ref={dateInputRef}
              type="date"
              className="task-date-native"
              value={task.date || ''}
              onChange={handleDateChange}
              aria-label={task.date ? `日付 ${task.date}（変更）` : '日付を設定'}
            />
          </div>
          {task.date && (
            <button
              type="button"
              className="task-date-clear"
              onClick={handleClearDate}
              onPointerDown={stopDatePropagation}
              aria-label="日付を解除"
              title="日付を解除"
            >
              ×
            </button>
          )}
        </div>
      )}

      <button className="task-delete-btn" onClick={handleDelete} aria-label="削除">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
